import { createSelector } from '@reduxjs/toolkit';
import {
  DownloadBatchState,
  DownloadFileState,
  DownloadsState,
} from './models';
import { DownloadSummary, summarizeFiles } from './summarize';
import { DownloadsStore, defaultState, namespace } from './store';

export * from './summarize';

export const selectStore = (state: object): DownloadsState =>
  (state as Partial<DownloadsStore>)[namespace] || defaultState;

export const selectDownloadBatches = createSelector(
  selectStore,
  (s): ReadonlyArray<DownloadBatchState> => s.batches
);

export const selectIsDownloadTrayOpen = createSelector(
  selectStore,
  (s) => s.isTrayOpen
);

export interface DownloadBatchSummary extends DownloadSummary {
  readonly id: string;
  /**
   * The file the progress line names. Three stream at once, so this is the
   * lowest-indexed one still going: it only changes when that file finishes,
   * where "most recently active" would flicker between three names. Falls back
   * to the first file so the line never blanks out and change the toast's height.
   */
  readonly currentName?: string;
}

/**
 * One summary per batch, oldest first. The progress toasts are driven from this
 * — one toast each, stacking in start order, each closing when its own batch
 * finishes rather than all of them waiting on the slowest.
 */
export const selectDownloadBatchSummaries = createSelector(
  selectDownloadBatches,
  (batches): ReadonlyArray<DownloadBatchSummary> =>
    batches.map((batch) => ({
      id: batch.id,
      currentName: (
        batch.files.find((file) => file.status === 'downloading') ??
        batch.files[0]
      )?.name,
      ...summarizeFiles(batch.files),
    }))
);

/** Every file across every batch, for the tray's flat list. */
export const selectDownloadFiles = createSelector(
  selectDownloadBatches,
  (batches): ReadonlyArray<DownloadFileState & { batchId: string }> =>
    batches.flatMap((batch) =>
      batch.files.map((file) => ({ ...file, batchId: batch.id }))
    )
);

/** True while any batch still has work to do — what the icon's spin follows. */
export const selectIsAnyDownloadActive = createSelector(
  selectDownloadBatchSummaries,
  (summaries) => summaries.some((summary) => summary.isActive)
);

/**
 * Whether the header bar should show its download icon at all. Tied to a batch
 * existing rather than to activity, so the icon survives the moment between the
 * last byte and the user clearing it.
 */
export const selectHasDownloadSession = createSelector(
  selectDownloadBatches,
  (batches) => batches.length > 0
);

/** Whether the tray should offer to clear anything. */
export const selectHasFinishedDownloads = createSelector(
  selectDownloadBatchSummaries,
  (summaries) => summaries.some((summary) => !summary.isActive)
);
